/**
 * Decomposition model for the plate.
 *
 * Relations, deliberately distinct:
 *   parent   - containment. The child lives INSIDE the parent and is hidden
 *              until the parent is opened.
 *   mode 0   - interface            (solid)
 *   mode 1   - produced by          (short dash)
 *   mode 2   - cut unresolved       (fine dot, flagged)
 *   mode 3   - offshoot             (long dash)
 *   mode 4   - contains             (faint solid, synthesised when open)
 *
 * links: every outbound destination a node owns. A node with links.live is a
 * WORLD. There is no separate portal field.
 *
 * standalone:true marks an ordinary project with no relationship established
 * yet; it deliberately carries no edge.
 *
 * WHAT BECOMES A NODE (Liz's rule):
 *   A thing with no onward connections stays inside its parent's box, as
 *   content you dig into. A thing that connects to something beyond its parent
 *   earns a node.
 *
 * DISPLAY LAYER. `id` is the stable key: the repo name GitHub knows, what
 * telemetry, parent and link endpoints all use. It is never renamed for looks.
 * Two optional fields sit on top of it:
 *   label - what a human reads (sprite, panel heading, lists). Absent: the id.
 *   role  - one line, in Liz's words, on what the node does in the system.
 * Always read a name through labelOf / labelOfId, never node.id, when drawing
 * text for a person.
 *
 * Anything not actually known is marked unknown:true rather than wired to a
 * plausible neighbour. priv:true means closed SOURCE: such a node never exposes links.repo.
 */
export const MODES = {
  INTERFACE: 0,
  PRODUCED_BY: 1,
  QUERY: 2,
  OFFSHOOT: 3,
  CONTAINS: 4, // synthesised at render time when an assembly is open
};

export const KINDS = [
  "method", "venture", "sys", "course",
  "silicon", "hardware", "flight", "pub", "dist", "cv",
];

const GH = "https://github.com/Lizo-RoadTown/";

export const nodes = [
  // ---- method, publication, record ------------------------------------
  { id: "IAKM", kind: "method", r: 11,
    note: "Interface-Anchored Knowledge Mapping",
    links: { doi: "https://doi.org/10.5281/zenodo.22677663" } },
  { id: "ASEE · Zenodo", kind: "pub", r: 7,
    note: "peer-reviewed paper + DOI-versioned artefacts",
    links: { paper: "https://peer.asee.org/engineering-pathways-in-education-and-the-credit-loss-challenge",
             doi: "https://doi.org/10.5281/zenodo.22668058" } },
  { id: "Curriculum Vitae", kind: "cv", r: 9,
    note: "full record — publications, research, training",
    links: { page: "/cv" } },

  // ---- KMICS: the whole ------------------------------------------------
  // Liz's name for the one system. The id is the display name; no expansion is
  // given anywhere, because none has been stated. No repo exists for it.
  // Five parts, each a child: Tapestry, The Lens, Loom, The Observer and the
  // Onboarding Assessment Lab. Role is her through-line.
  { id: "KMICS", kind: "sys", r: 16, defaultOpen: true,
    role: "one system: decompose, observe, remember, adapt, present" },

  // ---- Tapestry: the platform, absorbing the loom pieces ---------------
  { id: "tapestry", kind: "sys", r: 12, parent: "KMICS",
    label: "Tapestry",
    role: "the instrument: interface with the user, the projects and the observer; mission control",
    note: "system-of-record monorepo; the loom pieces migrate in as they mature",
    links: { repo: GH + "tapestry", live: "https://tapestry-khaki.vercel.app/" } },
  // Loom is a peer of Tapestry, not a part of it: Liz describes it as the
  // memory Tapestry interfaces with. Repo is private, so no links.repo.
  { id: "the-loom", kind: "sys", r: 8, parent: "KMICS", priv: true,
    label: "Loom", role: "the database with all the collective memory",
    note: "project registry · observatory · memory" },

  // The Observer: no repo exists (checked), which is not the same as a closed
  // one, so no priv flag. The note records the absence so it reads as a fact
  // and not an oversight. Deliberately wired to nothing: no edge has been stated.
  { id: "The Observer", kind: "sys", r: 8, parent: "KMICS",
    role: "the engine taking in all telemetry and readjusting for each project and the collective",
    note: "no repository" },
  { id: "Make_Skills", kind: "sys", r: 8, parent: "tapestry",
    note: "recursive skill engine", links: { repo: GH + "Make_Skills" } },
  { id: "loom-platform", kind: "sys", r: 7, parent: "tapestry",
    note: "platform site", links: { repo: GH + "loom-platform" } },
  { id: "project-starter", kind: "sys", r: 7, parent: "tapestry",
    note: "day-1 scaffolding: CLAUDE.md, MCPs, UX contract",
    links: { repo: GH + "project-starter" } },
  { id: "claude-skills-marketplace", kind: "sys", r: 7, parent: "tapestry",
    note: "agentskills.io standard", links: { repo: GH + "claude-skills-marketplace" } },
  { id: "tapestry-cli", kind: "dist", r: 6, parent: "tapestry",
    note: "pipx install tapestry-cli", links: { dist: "https://pypi.org/project/tapestry-cli/" } },
  { id: "tapestry-patterns", kind: "dist", r: 6, parent: "tapestry", note: "Claude Code plugin" },
  { id: "tapestry-discipline", kind: "dist", r: 6, parent: "tapestry", note: "Claude Code plugin" },

  // ---- what the public marketplace distributes --------------------------
  // claude-skills-marketplace (public) ships three plugins from its own
  // ./plugins tree, and lists a fourth, lens-log, from the lens-core repo.
  // Modelled the way education-plugins already is: the plugin's parent is the
  // part it belongs to, and the marketplace reaches it by a PRODUCED_BY edge.
  // Two plugins that still sit in the local cache are deliberately absent:
  // liz-patterns was RENAMED to tapestry-patterns, and loom-discipline was
  // superseded by tapestry-discipline. Both are already nodes above, and
  // claude-skills-marketplace/CLAUDE.md forbids re-adding either.
  { id: "ai-agents-architect", kind: "dist", r: 6, parent: "tapestry",
    note: "decision framework for agent architecture: single vs multi-agent, ReAct vs plan-and-execute, the autonomy spectrum",
    links: { repo: GH + "claude-skills-marketplace/tree/main/plugins/ai-agents-architect" } },
  { id: "onboarding-psychologist", kind: "dist", r: 6, parent: "tapestry",
    note: "designs first-time-user flows on the identity-to-habit arc, grounded in Fogg",
    links: { repo: GH + "claude-skills-marketplace/tree/main/plugins/onboarding-psychologist" } },
  { id: "sde-extraction-guard", kind: "dist", r: 6, parent: "tapestry",
    note: "hook-enforced schema guard: validates the extraction surface after every edit",
    links: { repo: GH + "claude-skills-marketplace/tree/main/plugins/sde-extraction-guard" } },

  // ---- The Lens --------------------------------------------------------
  { id: "lens-core", kind: "sys", r: 11, parent: "KMICS",
    label: "The Lens", role: "automated decomposition of the sociotechnical system",
    note: "shared database shape + module composition", links: { repo: GH + "lens-core" } },
  { id: "lens-ingest", kind: "sys", r: 7, parent: "lens-core", links: { repo: GH + "lens-ingest" } },
  { id: "lens-review", kind: "sys", r: 7, parent: "lens-core", links: { repo: GH + "lens-review" } },
  // Distributed through the public marketplace rather than from lens-core, which
  // is what carries it across a boundary and so earns it a node.
  { id: "lens-log", kind: "dist", r: 6, parent: "lens-core",
    note: "enforces a file-based log of decomposition work: any turn touching a surface declared in decomposition.json is written down",
    links: { repo: GH + "lens-core/tree/main/plugins/lens-log" } },
  { id: "lens-observe", kind: "sys", r: 7, parent: "lens-core",
    note: "computes active / orphaned / degrading / blind", links: { repo: GH + "lens-observe" } },
  { id: "lens-serve", kind: "sys", r: 7, parent: "lens-core",
    note: "query the verified library via API + MCP", links: { repo: GH + "lens-serve" } },
  { id: "Knowledge Observatory", kind: "sys", r: 8,
    note: "the method's first instrument; became The Lens",
    links: { live: "https://knowledge-observatory-docs.onrender.com/" } },

  // ---- PROVES ----------------------------------------------------------
  { id: "PROVES", kind: "sys", r: 10, note: "PROVES Kit work" },
  { id: "PROVES_LIBRARY", kind: "sys", r: 7, parent: "PROVES", links: { repo: GH + "PROVES_LIBRARY" } },
  { id: "proves-curation-dashboard", kind: "sys", r: 7, parent: "PROVES",
    note: "the 3D graph this plate descends from",
    links: { repo: GH + "proves-curation-dashboard", live: "https://proves-curation-dashboard.vercel.app/" } },
  { id: "proveskit-agent", kind: "flight", r: 8, parent: "PROVES",
    note: "agentic AI for F′ flight software", links: { repo: GH + "proveskit-agent" } },

  // ---- offshoots -------------------------------------------------------
  { id: "fall-2026-hub", kind: "sys", r: 10, priv: true,
    note: "7 courses · Canvas crawler · book layer · embedded agent" },
  { id: "CHE 2301", kind: "course", r: 6, parent: "fall-2026-hub", note: "Process and Automation" },
  { id: "MFE 2010", kind: "course", r: 6, parent: "fall-2026-hub", note: "Manufacturing Systems and Process" },
  { id: "MTE 2070", kind: "course", r: 6, parent: "fall-2026-hub", note: "Materials Science and Engineering" },
  { id: "IME 2390", kind: "course", r: 6, parent: "fall-2026-hub", note: "Industrial Costs & Controls" },
  { id: "IME 3261", kind: "course", r: 6, parent: "fall-2026-hub", note: "Production Planning and Control" },
  { id: "summer-2026-hub", kind: "sys", r: 10, priv: true },
  { id: "Organometallics", kind: "course", r: 6, parent: "summer-2026-hub",
    note: "summer class; connects to MTE 2070" },
  { id: "Circuit Lab", kind: "sys", r: 8, priv: true, note: "3D circuit teaching instrument" },
  // Spring 2026, which predates both teaching hubs: it was built on its own and
  // no hub existed to hold it. standalone, and NOT wired to fall-2026-hub.
  { id: "Interactive LP Tool", kind: "sys", r: 8, standalone: true,
    note: "spring 2026 — interactive linear-programming teaching tool, built standalone before any hub",
    links: { repo: GH + "Interactivelinearprogrammingui", live: "https://lp-web.onrender.com/" } },

  // ---- standalone ------------------------------------------------------
  // priv means the SOURCE is closed (the repo is private); the product at
  // humancensys.com is public, so it keeps links.live (it is a world).
  { id: "Human Centered Systems", kind: "venture", r: 11, priv: true,
    note: "R&D for Complex Systems", links: { live: "https://humancensys.com" } },
  { id: "sde-extraction", kind: "sys", r: 7, note: "SDE extraction, loom-aware",
    links: { repo: GH + "sde-extraction", live: "https://sde-extraction.vercel.app/" } },
  { id: "file_queuing_system", kind: "sys", r: 7,
    note: "two-stage reproducibility review coordination",
    links: { repo: GH + "file_queuing_system" } },
  { id: "amp-microgravity-site", kind: "sys", r: 7, note: "SSEP Mission 21 companion site",
    links: { repo: GH + "amp-microgravity-site", live: "https://amp-microgravity-site.onrender.com/" } },

  // ---- module addition to the lens and tapestry -------------------------
  // Liz: "the Onboarding-Assessment-lab which is a module addition to the lens
  // and tapestry" and "That is the education lab, but it will be delivered as
  // an onboarding assessment lab." The repo (education-lab) is private, so no
  // links.repo. Two onward connections, so it earns a node.
  // Its id already reads as a name, so it carries no label; the fallback shows it.
  { id: "Onboarding Assessment Lab", kind: "sys", r: 8, priv: true, parent: "KMICS",
    role: "collects an individual's style over time and skills each project agent to fit",
    note: "the education lab, delivered as an onboarding assessment lab" },

  // ---- the education plugins: the lab's marketplace and what it ships ------
  // From Liz's project record. The marketplace repo (Lizo-RoadTown/education-plugins,
  // marketplace name education-lab) is private, so it and the plugins it
  // distributes carry no links. The plugins earn nodehood because the fall-2026
  // class repos consume them; no node stands for those repos, so that
  // consumption is deliberately NOT drawn (fall-2026-hub is a neighbour, not them).
  { id: "education-plugins", kind: "sys", r: 7, priv: true, parent: "Onboarding Assessment Lab",
    note: "private marketplace (education-lab) distributing the three education plugins" },
  { id: "learning-core", kind: "dist", r: 6, parent: "Onboarding Assessment Lab",
    note: "every class: learner profile, source fidelity, and 148 tagged learning rules with a query tool; soft page-linter hook" },
  { id: "stem-walkthroughs", kind: "dist", r: 6, parent: "Onboarding Assessment Lab",
    note: "lecture courses: the canonical course-walkthrough template and study guide" },
  { id: "discovery-apps", kind: "dist", r: 6, parent: "Onboarding Assessment Lab",
    note: "classes that build hands-on learning apps: the discovery-app design procedure" },

  // ---- hardware -------------------------------------------------------
  // Ordinary projects that simply have no world yet. They carry no edge
  // because no relationship has been established.
  { id: "tiny-tapeout", kind: "silicon", r: 9, standalone: true,
    note: "custom ASIC, Verilog → GDS, submitted for fabrication",
    links: { repo: GH + "tiny-tapeout" } },
  { id: "mini-pupper", kind: "hardware", r: 8, standalone: true,
    note: "quadruped, ROS 2 on Raspberry Pi", links: { repo: GH + "mini-pupper" } },

  // ---- NASA Space Apps -------------------------------------------------
  { id: "signal-and-structure", kind: "sys", r: 9, standalone: true,
    note: "NASA Space Apps Challenge — web, server and Python engine",
    links: { repo: GH + "signal-and-structure" } },
];

const L = [
  ["IAKM", "lens-core", 0],
  ["IAKM", "ASEE · Zenodo", 1],
  ["IAKM", "Curriculum Vitae", 1],
  ["IAKM", "Human Centered Systems", 0],
  ["IAKM", "Knowledge Observatory", 1],
  ["IAKM", "file_queuing_system", 1],

  ["lens-core", "lens-ingest", 0],
  ["lens-core", "lens-observe", 0],
  ["lens-core", "lens-serve", 0],
  ["lens-core", "lens-review", 2],
  ["lens-ingest", "lens-review", 2],
  ["lens-review", "lens-observe", 0],
  ["lens-observe", "lens-serve", 0],

  ["tapestry", "lens-core", 0],
  ["tapestry", "Human Centered Systems", 0],
  ["tapestry", "PROVES", 0],
  ["tapestry", "sde-extraction", 1],
  ["tapestry", "tapestry-cli", 1],
  ["the-loom", "Make_Skills", 0],
  ["the-loom", "loom-platform", 1],
  ["the-loom", "tapestry-discipline", 1],
  ["the-loom", "project-starter", 1],
  ["Make_Skills", "claude-skills-marketplace", 1],
  // what the public marketplace distributes
  ["claude-skills-marketplace", "ai-agents-architect", 1],
  ["claude-skills-marketplace", "onboarding-psychologist", 1],
  ["claude-skills-marketplace", "sde-extraction-guard", 1],
  ["claude-skills-marketplace", "lens-log", 1],
  // the guard runs against that repo's extraction surface
  ["sde-extraction-guard", "sde-extraction", 0],
  ["Make_Skills", "tapestry-patterns", 1],

  ["PROVES_LIBRARY", "proves-curation-dashboard", 0],
  ["proveskit-agent", "PROVES_LIBRARY", 1],

  ["amp-microgravity-site", "ASEE · Zenodo", 1],

  ["Make_Skills", "fall-2026-hub", 3],
  ["Make_Skills", "summer-2026-hub", 3],
  ["Organometallics", "MTE 2070", 0],
  ["fall-2026-hub", "Circuit Lab", 3],
  // Module addition to both systems; read as interface, not produced-by.
  ["lens-core", "Onboarding Assessment Lab", 0],
  ["tapestry", "Onboarding Assessment Lab", 0],
  // The flow between the five parts of KMICS (docs/architecture/system-architecture.md,
  // and Liz's own descriptions of each part). Source produced target in mode 1.
  // The Lens PRODUCES the Observer's implementations (lens-observe is the watcher
  // engine, lens-review + lens-serve the Observatory console); generative, not
  // supply-of-data.
  ["lens-core", "The Observer", 1],
  ["The Observer", "the-loom", 0],            // the engine reads and writes the memory
  ["Onboarding Assessment Lab", "the-loom", 0], // style learned over time is kept in the memory
  ["tapestry", "The Observer", 0],            // mission control: the engine's state made operable
  ["tapestry", "the-loom", 0],                // the instrument surfaces the memory
  // The marketplace distributes the three plugins (source produces target).
  ["education-plugins", "learning-core", 1],
  ["education-plugins", "stem-walkthroughs", 1],
  ["education-plugins", "discovery-apps", 1],
  ["proves-curation-dashboard", "lens-core", 3],
  ["Knowledge Observatory", "lens-core", 3],
];

export const links = L.map(([source, target, mode]) => ({ source, target, mode }));

const byIdForLabels = new Map(nodes.map((n) => [n.id, n]));

/** The name a human reads for a node: its label, else its id. */
export const labelOf = (node) => (node && node.label ? node.label : node && node.id);

/** labelOf by id, for references (a parent, a link end). Unknown ids pass through. */
export const labelOfId = (id) => {
  const n = byIdForLabels.get(id);
  return n ? labelOf(n) : id;
};
